import type { AnalyticsEventPayload } from '@atlaskit/editor-common/analytics';
import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { ExperienceTrackerHandler } from '@atlassian/conversation-assistant-instrumentation/main';

import { replaceMauiApp } from '../editor-commands/replace-maui-app';
import type {
	MalleableUiPlugin,
	PluginConfiguration,
	ReplaceMauiAppProps,
} from '../malleableUiPluginType';

// Analytics payload shape used by both this action and the paste-time
// duplicate workflow.
type DuplicateAnalyticsPayload = {
	action: 'succeeded' | 'failed';
	actionSubject: 'mauiAppDuplicate';
	actionSubjectId: 'mauiInlineEdit';
	attributes: Record<string, unknown>;
	eventType: EVENT_TYPE;
};

export type DuplicateAndReplaceMauiApp = (props: ReplaceMauiAppProps) => Promise<void>;

/**
 * Builds the duplicate-and-replace action used when Rovo replaces a MaUI App.
 *
 * Convo-ai returns the id of a freshly created MAUI app via the streaming
 * `ConfluenceUpdatePageCommand`. Inserting that id directly leaves the new
 * app with its original (system-space) permissions. We must duplicate it
 * under the current page so it inherits the page perms before swapping the
 * URL on the existing extension node.
 */
export function makeDuplicateAndReplaceMauiApp({
	api,
	config,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin> | undefined;
	config: PluginConfiguration | undefined;
}): DuplicateAndReplaceMauiApp {
	return async ({ previousMauiId, newMauiId, localId }) => {
		let idToInsert = newMauiId;

		if (fg('cc-maui-phase-2')) {
			// NOTE: duplicate dependencies currently live nested under
			// `config.diagramPaste` because the paste-time duplicate flow was
			// the first consumer. A follow-up PR will lift these to the top
			// of `PluginConfiguration` so both flows share a single config
			// surface — see the PR description for the planned refactor.
			const duplicateConfig = config?.diagramPaste;
			const duplicateMauiApp = duplicateConfig?.duplicateMauiApp;
			const containerContentId = duplicateConfig?.getCurrentPageId?.();
			const contentType = duplicateConfig?.getCurrentContentType?.();

			if (duplicateMauiApp && containerContentId) {
				const experienceTracker = ExperienceTrackerHandler.getInstance().experienceTracker;
				const experienceAttributes = {
					appId: newMauiId,
					currentPageId: containerContentId,
					source: 'mauiInlineEdit',
				};

				experienceTracker.start({
					name: ExperienceName.REMIX_DUPLICATE_APP,
					id: ExperienceName.REMIX_DUPLICATE_APP,
					attributes: experienceAttributes,
				});

				try {
					const { id } = await duplicateMauiApp(newMauiId, containerContentId, contentType);
					idToInsert = id;

					fireAnalytics(api, {
						eventType: EVENT_TYPE.OPERATIONAL,
						actionSubject: 'mauiAppDuplicate',
						action: 'succeeded',
						actionSubjectId: 'mauiInlineEdit',
						attributes: { originalId: newMauiId, newId: id },
					});

					experienceTracker.succeed({
						name: ExperienceName.REMIX_DUPLICATE_APP,
						attributes: { ...experienceAttributes, newAppId: id },
					});
				} catch (error) {
					fireAnalytics(api, {
						eventType: EVENT_TYPE.OPERATIONAL,
						actionSubject: 'mauiAppDuplicate',
						action: 'failed',
						actionSubjectId: 'mauiInlineEdit',
						attributes: {
							originalId: newMauiId,
							errorMessage: (error as Error)?.message,
						},
					});

					const duplicateError =
						error instanceof Error ? error : new Error('Failed to duplicate MAUI app');
					experienceTracker.fail({
						name: ExperienceName.REMIX_DUPLICATE_APP,
						error: duplicateError,
						attributes: experienceAttributes,
					});
					experienceTracker.fail({
						name: ExperienceName.REMIX_INSERT,
						error: duplicateError,
						attributes: {
							...experienceAttributes,
							failedDueTo: ExperienceName.REMIX_DUPLICATE_APP,
						},
					});
				}
			}
		}

		api?.core?.actions.execute(replaceMauiApp({ previousMauiId, newMauiId: idToInsert, localId }));
	};
}

function fireAnalytics(
	api: ExtractInjectionAPI<MalleableUiPlugin> | undefined,
	payload: DuplicateAnalyticsPayload,
) {
	api?.analytics?.actions.fireAnalyticsEvent(payload as unknown as AnalyticsEventPayload);
}
